import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import styled from 'styled-components';
import { pdf } from '@react-pdf/renderer';
import saveAs from 'file-saver';
import { useHistory, useLocation } from 'react-router-dom';
import { Dropdown, Grid, Icon, Loader, Menu, Popup, Table } from 'semantic-ui-react';

import { getQuotes } from '../../../services/fire-insurance-budget';
import { FireInsuranceAnalysisStatusEnum, translateStatus } from '../utils/Pottencial';
import DetailModal from './Details/DetailModal';
import EditModal from './Edit/EditModal';
import { FireInsuranceQuoteFiltersInterface } from '../util';
import { statusList } from '../utils/Pottencial';
import { maskCpfOrCnpj } from '../../../services/masks';
import CancelModal from './Cancel/CancelModal';
import {
  AvailableBrokersAndEstatesState,
  CurrentPageState
} from '../../../store/types/temp-types';
import { iRootDispatch, iRootState } from '../../../store';
import { CustoDropDownItemProps } from '../../../components/types';
import { FireInsuranceAnalysisType } from '../types/fireInsuranceAnalysisType';
import { ActionEnum } from '../../../enums/authz-action.enum';
import AuthorizationContainer from '../../../components/AuthorizationContainer';
import { FeatureEnum } from '../../../enums/authz-feature.enum';
import IconButton from '../../../components/IconButton';
import { InsurersCodeEnum } from '../../../enums/insurers-code.enum';
import AlfaBudgetReport from '../../../services/reports/AlfaBudgetReport';
import FireGeneralCondition from '../budget/GeneralConditionModal';
import { BudgetContext } from '../budget/forms-based-in-insurer/Alfa';
import { isBackOfficeUser } from '../../../utils/isBackOfficeUser';
import TablePaginationMenu from '../../../components/TablePaginationMenu';
import { PushRoute } from '../../../util';
import { get } from '../../../services/request';
import { dangerNotification, warningNotification } from '../../../services/notification';
import PopupModal from '../../../components/PopupModal';
import { BannerLocationActivationEnum } from '../../../types/Banner';
import { useSelector } from 'react-redux';
import LenderInsuranceUpdateModal from '../components/LenderInsuranceUpdateModal';
import { calculateAge } from '../../../utils/calculateAge';
import EstateUnitFilter from '../../../components/EstateUnitFilter';
import { getListEmptyMessage } from '../../../utils/estateUnitEmptyState.util';
import { appendEstateUnitIdToFilter } from '../../../utils/estateUnitFilter.util';
import { typeOptions } from '../policy-list/types';
import { UserRolesEnum } from '../../../enums/user-roles.enum';
import FilterBar, { FilterField, FilterValues } from '../../../components/Filter';

type SearchField = 'document' | 'name' | 'address';

type ApiFilters = {
  name?: string;
  document?: string;
  address?: string;
  insurerId?: string;
  estateId?: string;
  status?: string;
  start?: string;
  end?: string;
  estateUnitId?: string;
};

const SEARCH_FIELDS: SearchField[] = ['document', 'name', 'address'];

const ClickableTableCell = styled(Table.Cell)`
  cursor: pointer;
  position: relative;
`;

const ClickableTableRow = styled(Table.Row)`
  :hover {
    background-color: #f0f0f0;
  }
`;

interface CopyCancelledBudget {
  budgetId: string;
  estateId: string;
  insurerId: string;
  context: string;
}

const toSelectOptions = (
  items: Array<{ value: any; text: string }>
): Array<{ value: string; label: string }> =>
  items.map(item => ({
    value: String(item.value),
    label: String(item.text)
  }));

const formatDateValue = (value: any): string | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) {
    return moment(value).format('YYYY-MM-DD');
  }
  const asString = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(asString)) {
    return asString.substring(0, 10);
  }
  const parsed = moment(asString, ['YYYY-MM-DD', 'DD/MM/YYYY'], true);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : asString;
};

const BudgetList = (props: {
  availableBrokersAndEstates: AvailableBrokersAndEstatesState;
  updateCurrentPage: Function;
  user: iRootState['user'];
}) => {
  const { availableBrokersAndEstates } = props;

  const estatesOptions: CustoDropDownItemProps[] = availableBrokersAndEstates.estates;
  const insurerOptions: CustoDropDownItemProps[] =
    availableBrokersAndEstates.insurers.filter(insurer =>
      insurer.value.availability.includes('FIRE_INSURANCE')
    );

  const [data, setData] = useState<FireInsuranceAnalysisType[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [count, setCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedFireInsurance, setSelectedFireInsurance] = useState<any>();
  const [openDetail, setOpenDetail] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openCancelModal, setOpenCancelModal] = useState<boolean>(false);
  const [sendHiring, setSendHiring] = useState<boolean>(false);
  const [openConditions, setOpenConditions] = useState<boolean>(false);
  const [loadingBudgetPdfId, setLoadingBudgetPdfId] = useState<string | null>(null);

  const history = useHistory();
  const location = useLocation();
  // APPROVED permite contratar; CONTRACT_REQUESTED permite editar/cancelar
  // mas não contratar novamente (a contratação já foi enfileirada)
  const ableToHire = ['APPROVED'];
  const ableToEdit = [
    'APPROVED',
    'CONTRACT_REQUESTED',
    'ERROR',
    'DENIED',
    'WAITING',
    '',
  ];
  const [openLenderInsuranceModal, setOpenLenderInsuranceModal] = useState<boolean>(false);
  const [lenderInsuranceRate, setLenderInsuranceRate] = useState<number>(0);

  const isBackOffice = isBackOfficeUser();
  const isEstateQuotationUser = props.user?.role === UserRolesEnum.estateQuotation;

  // Check if lender insurance is enabled for the estate
  const lenderInsuranceEnabled = useSelector(
    (state: iRootState) => state.availableBrokersAndEstates.lenderInsuranceEnabled ?? false
  );

  const singleEstateId = useMemo(() => {
    if (estatesOptions.length !== 1) return '';
    const value = estatesOptions[0]?.value;
    return String(typeof value === 'object' && value?.id ? value.id : value || '');
  }, [estatesOptions]);

  const initialSearchParams = new URLSearchParams(location.search);
  const initialId = initialSearchParams.get('id');
  const initialFireInsuranceId = initialSearchParams.get('fireInsuranceId');
  const hasDeepLink = Boolean(initialId || initialFireInsuranceId);

  const [id, setId] = useState<string | null>(initialId);
  const [fireInsuranceId, setFireInsuranceId] = useState<string | null>(
    initialFireInsuranceId
  );

  const initialFilterValues = useMemo(
    (): FilterValues => ({
      type: 'name',
      argument: '',
      insurerId: '',
      estateId: singleEstateId,
      status: '',
      start: '',
      end: '',
      estateUnitId: ''
    }),
    [singleEstateId]
  );

  const insurerSelectOptions = useMemo(
    () =>
      insurerOptions.map(item => ({
        value: String(item.value?.id || ''),
        text: String(item.text || '')
      })),
    [insurerOptions]
  );

  const estateSelectOptions = useMemo(
    () =>
      estatesOptions.map(item => ({
        value: String(item.value?.id || ''),
        text: String(item.text || '')
      })),
    [estatesOptions]
  );

  const normalizeToApiFilter = useCallback(
    (values: FilterValues): ApiFilters => {
      const searchType = (values.type as SearchField) || 'name';
      let argument = String(values.argument || '').trim();

      if (searchType === 'document' && argument) {
        argument = argument.replace(/\D/g, '');
      }

      const filter: ApiFilters = {};

      if (argument && SEARCH_FIELDS.includes(searchType)) {
        filter[searchType] = argument;
      }

      if (values.insurerId) filter.insurerId = String(values.insurerId);

      const selectedEstateId = values.estateId ? String(values.estateId) : '';
      if (selectedEstateId) {
        filter.estateId = selectedEstateId;
      } else if (singleEstateId) {
        filter.estateId = singleEstateId;
      }

      if (values.status) filter.status = String(values.status);

      const start = formatDateValue(values.start);
      const end = formatDateValue(values.end);
      if (start) filter.start = start;
      if (end) filter.end = end;

      return appendEstateUnitIdToFilter(
        filter as Record<string, unknown>,
        String(values.estateUnitId || '')
      ) as ApiFilters;
    },
    [singleEstateId]
  );

  const [appliedFilters, setAppliedFilters] = useState<ApiFilters>(() =>
    normalizeToApiFilter(initialFilterValues)
  );
  const [isEnabledToFetch, setIsEnabledToFetch] = useState(hasDeepLink || !isBackOffice);

  const appliedFiltersRef = useRef(appliedFilters);
  const idRef = useRef(id);
  const fireInsuranceIdRef = useRef(fireInsuranceId);

  useEffect(() => {
    appliedFiltersRef.current = appliedFilters;
  }, [appliedFilters]);

  useEffect(() => {
    idRef.current = id;
  }, [id]);

  useEffect(() => {
    fireInsuranceIdRef.current = fireInsuranceId;
  }, [fireInsuranceId]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const nextId = params.get('id');
    const nextFireInsuranceId = params.get('fireInsuranceId');
    if (nextId || nextFireInsuranceId) {
      setId(nextId);
      setFireInsuranceId(nextFireInsuranceId);
      setIsEnabledToFetch(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (!isBackOffice) {
      setIsEnabledToFetch(true);
    }
  }, [isBackOffice]);

  const filterFields: FilterField[] = useMemo(() => {
    const fields: FilterField[] = [
      {
        kind: 'search',
        name: 'search',
        label: 'Pesquisar',
        placeholder: 'Digite...',
        defaultType: 'name',
        types: typeOptions.map(option => ({
          value: String(option.value),
          label: String(option.text)
        })),
        width: 1.6
      },
      {
        kind: 'select',
        name: 'insurerId',
        label: 'Seguradora',
        placeholder: 'Selecionar...',
        options: toSelectOptions(insurerSelectOptions),
        getDisplayValue: values => {
          const option = insurerSelectOptions.find(
            item => String(item.value) === String(values.insurerId)
          );
          return option ? option.text : undefined;
        }
      }
    ];

    if (isBackOffice) {
      fields.push({
        kind: 'select',
        name: 'estateId',
        label: 'Imobiliária',
        placeholder: 'Selecionar...',
        options: toSelectOptions(estateSelectOptions),
        getDisplayValue: values => {
          const option = estateSelectOptions.find(
            item => String(item.value) === String(values.estateId)
          );
          return option ? option.text : undefined;
        }
      });
    }

    fields.push(
      {
        kind: 'select',
        name: 'status',
        label: 'Status',
        placeholder: 'Selecionar...',
        options: toSelectOptions(statusList),
        getDisplayValue: values => {
          const option = statusList.find(
            item => String(item.value) === String(values.status)
          );
          return option ? option.text : undefined;
        }
      },
      {
        kind: 'dateRange',
        name: 'period',
        label: 'Período',
        startName: 'start',
        endName: 'end'
      },
      {
        kind: 'custom',
        name: 'estateUnitId',
        label: 'Unidade',
        advanced: true,
        getDisplayValue: values => {
          if (!values.estateUnitId) return undefined;
          if (values.estateUnitId === 'null') return 'Sem unidade';
          return String(values.estateUnitId);
        },
        render: ({ values, setValue }) => (
          <EstateUnitFilter
            estateId={String(values.estateId || singleEstateId || '')}
            value={values.estateUnitId || ''}
            onChange={value => setValue('estateUnitId', value)}
            upward
          />
        )
      }
    );

    return fields;
  }, [isBackOffice, insurerSelectOptions, estateSelectOptions, singleEstateId]);

  const handleOpenLenderInsuranceModal = (item: any) => {
    setSelectedFireInsurance(item);
    if (item?.fireInsurance?.estate?.lenderInsuranceRate) {
      setLenderInsuranceRate(Number(item.fireInsurance.estate.lenderInsuranceRate));
    }
    setOpenLenderInsuranceModal(true);
  };

  const handleCloseLenderInsuranceModal = () => {
    setSelectedFireInsurance(undefined);
    setOpenLenderInsuranceModal(false);
    getQuotations();
  };

  const toggleOpenConditions = () => {
    setOpenConditions(true);
  };

  const toggleCloseConditions = () => {
    setOpenConditions(false);
  };

  const redirectToBondInsuranceBudget = (fireInsuranceId: string) => {
    PushRoute(history, props, `bond-insurance`, {
      fireInsuranceId
    });
  };

  const openDetailModal = (fireInsurance: any) => {
    setSelectedFireInsurance(fireInsurance);
    setOpenDetail(true);
  };

  const clearDeepLink = useCallback(() => {
    setId(null);
    setFireInsuranceId(null);

    const params = new URLSearchParams(location.search);
    params.delete('id');
    params.delete('fireInsuranceId');
    const search = params.toString();

    history.replace({
      pathname: location.pathname,
      search: search ? `?${search}` : ''
    });
  }, [history, location.pathname, location.search]);

  const getQuotations = useCallback(async () => {
    setIsLoading(true);

    const filter = {
      ...appliedFiltersRef.current,
      page: currentPage,
      isRv: true,
      id: idRef.current || undefined,
      fireInsuranceId: fireInsuranceIdRef.current || undefined
    };

    const result = await getQuotes(filter as FireInsuranceQuoteFiltersInterface);

    if (result?.data) {
      setData(result.data);
      if ((idRef.current || fireInsuranceIdRef.current) && result.data.length === 1) {
        openDetailModal(result.data[0]);
      }
    }

    if (result?.quantity !== undefined && currentPage === 1) {
      setCount(result.quantity);
    }

    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    if (!isEnabledToFetch) return;
    getQuotations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, appliedFilters, isEnabledToFetch, getQuotations]);

  const handleFilterSearch = (values: FilterValues) => {
    const next = normalizeToApiFilter(values);
    setAppliedFilters(next);
    setCurrentPage(1);
    setIsEnabledToFetch(true);
    if (idRef.current || fireInsuranceIdRef.current) {
      clearDeepLink();
    }
  };

  const handleFilterClear = () => {
    setCurrentPage(1);
    setIsEnabledToFetch(true);
    if (idRef.current || fireInsuranceIdRef.current) {
      clearDeepLink();
    }
  };

  const closeDetailModal = () => {
    setSelectedFireInsurance(undefined);
    setSendHiring(false);
    setOpenDetail(false);
  };

  const closeEditModal = () => {
    setSelectedFireInsurance(undefined);
    setOpenEdit(false);
    getQuotations();
  };

  const handleOpenCancelModal = (fireInsurance: any) => {
    setSelectedFireInsurance(fireInsurance);
    setOpenCancelModal(true);
  };

  const closeCancelModal = () => {
    setOpenCancelModal(false);
    setSelectedFireInsurance(undefined);
  };

  async function getSegimobBudgetPdf(
    segimobEstateId: string,
    segimobBudgetId: string
  ) {
    const response = await get(
      `/fire-insurance-quotations/segimob/budget/pdf?segimobEstateId=${segimobEstateId}&segimobBudgetId=${segimobBudgetId}`
    );
    return response;
  }

  async function getYelumBudgetPdf(budgetId: string) {
    const response = await get(`/fire-insurance-proposals/yelum-seguros/budget/${budgetId}/pdf`);
    return response;
  }

  const handleFireInsuranceBudgetReport = async (item: any) => {
    if (item.insurer.code === InsurersCodeEnum.alfa) {
      const blob = await pdf(<AlfaBudgetReport data={item} />).toBlob();
      saveAs(blob, `ORÇAMENTO_${new Date().getTime()}.pdf`);
    } else if (item.insurer.code === InsurersCodeEnum.Yelum) {
      try {
        setLoadingBudgetPdfId(item.id);
        const { base64, document } = await getYelumBudgetPdf(item.id);
        const bytes = atob(base64);
        const byteNumbers = new Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) {
          byteNumbers[i] = bytes.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        saveAs(blob, document || `ORÇAMENTO_${new Date().getTime()}.pdf`);
      } catch (error) {
        warningNotification(
          'Impressão do orçamento não disponível',
          'Não foi possível gerar o documento agora, por favor tente novamente.'
        );
      } finally {
        setLoadingBudgetPdfId(null);
      }
    } else if (item.insurer.code === InsurersCodeEnum.TokioMarineSegimob) {
      try {
        if (!item.fireInsurance.estate.segimobCode || !item.referenceCode) {
          dangerNotification(
            'Impressão do orçamento não disponível',
            'O documento não foi encontrado!'
          );
          return;
        }
        setLoadingBudgetPdfId(item.id);
        const { base64, nomeArquivo } = await getSegimobBudgetPdf(
          item.fireInsurance.estate.segimobCode,
          item.referenceCode
        );
        const bytes = atob(base64);
        const byteNumbers = new Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) {
          byteNumbers[i] = bytes.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        saveAs(blob, nomeArquivo);
      } catch (error) {
        warningNotification(
          'Impressão do orçamento não disponível',
          'O documento não foi encontrado, por favor, tente novamente mais tarde.'
        );
      } finally {
        setLoadingBudgetPdfId(null);
      }
    }
  };

  const handleCopyCancelledBudget = async (copyCancelledBudget: CopyCancelledBudget) => {
    const { budgetId, estateId, insurerId, context } = copyCancelledBudget;

    props.updateCurrentPage({
      id: 4,
      clickHandler: (e: any, data: any) => { },
      name: 'fire-insurance-budget',
      route: 'fire-insurance-budget',
      text: 'Edição de Cotação',
      title: 'Edição de orçamento de seguro incêndio'
    });

    history.push(
      `/admin/fire-insurance-budget/${budgetId}?context=${context}&estateId=${estateId}&insurerId=${insurerId}`
    );
  };

  return (
    <>
      <Grid columns={'equal'}>
        <Grid.Row
          style={{ textAlign: 'right', justifyContent: 'right', paddingRight: 10 }}
        >
          <FireGeneralCondition
            openModal={openConditions}
            toggleOpen={toggleOpenConditions}
            toggleClose={toggleCloseConditions}
          />
        </Grid.Row>
      </Grid>

      <div style={{ margin: '10px 0 16px' }}>
        <FilterBar
          fields={filterFields}
          initialValues={initialFilterValues}
          onSearch={handleFilterSearch}
          onClear={handleFilterClear}
          loading={isLoading}
          syncToUrl
          urlNamespace="fire-budgets"
          hasExternalActiveFilters={Boolean(id || fireInsuranceId)}
        />
      </div>

      <Table celled>
        <Table.Header fullWidth>
          <Table.HeaderCell textAlign={'center'}>Solicitado em</Table.HeaderCell>
          <Table.HeaderCell textAlign={'center'} width={2}>
            Seguradora
          </Table.HeaderCell>
          <Table.HeaderCell textAlign={'center'}>Imobiliária</Table.HeaderCell>
          <Table.HeaderCell textAlign={'center'}>Inquilino</Table.HeaderCell>
          <Table.HeaderCell textAlign={'center'}>Endereço</Table.HeaderCell>
          <Table.HeaderCell textAlign={'center'}>Status</Table.HeaderCell>
          <Table.HeaderCell textAlign={'center'}>Valor</Table.HeaderCell>
          <Table.HeaderCell textAlign={'center'}>Ações</Table.HeaderCell>
        </Table.Header>
        <Table.Body>
          {isLoading ? (
            <Table.Row>
              <Table.Cell textAlign={'center'} colSpan={7}>
                <Loader active={isLoading} inline />
              </Table.Cell>
            </Table.Row>
          ) : (
            <>
              {data.length ? (
                <>
                  {data.map((item, index) => {
                    const isCanceled =
                      item.status === FireInsuranceAnalysisStatusEnum.Canceled;

                    return (
                      <ClickableTableRow key={index}>
                        <ClickableTableCell
                          onClick={() => openDetailModal(item)}
                          textAlign={'center'}
                        >
                          {moment(item.created_at).format('DD/MM/YYYY')}
                        </ClickableTableCell>

                        <ClickableTableCell
                          onClick={() => openDetailModal(item)}
                          textAlign={'center'}
                        >
                          {item.insurer.name}
                        </ClickableTableCell>

                        <ClickableTableCell
                          onClick={() => openDetailModal(item)}
                          textAlign={'center'}
                        >
                          {item.fireInsurance.estate.name}
                        </ClickableTableCell>

                        <ClickableTableCell
                          onClick={() => openDetailModal(item)}
                          textAlign={'center'}
                        >
                          {item.fireInsurance.tenantName}
                          <br />
                          <span style={{ color: '#777' }}>
                            ({maskCpfOrCnpj(item.fireInsurance.tenantDocument)})
                          </span>
                        </ClickableTableCell>

                        <ClickableTableCell
                          onClick={() => openDetailModal(item)}
                          textAlign={'center'}
                        >
                          {`${item.fireInsurance.propertyAddresses.street}, ${item.fireInsurance.propertyAddresses.number
                            }${item.fireInsurance.propertyAddresses?.complement
                              ? ` - ${item.fireInsurance.propertyAddresses.complement}`
                              : ''
                            }`}
                        </ClickableTableCell>

                        <ClickableTableCell
                          onClick={() => openDetailModal(item)}
                          textAlign={'center'}
                        >
                          {item.status === 'ERROR' ? (
                            <Popup
                              header={'Erro'}
                              content={item.observations}
                              position={'left center'}
                              trigger={
                                <span
                                  style={{
                                    color: item.status.length
                                      ? 'rgba(0, 0, 0, .87)'
                                      : '#ccc'
                                  }}
                                >
                                  {translateStatus(item.status)}
                                </span>
                              }
                            />
                          ) : item.validationStatus === 'warning' ||
                            item.validationStatus === 'error' ? (
                            <Popup
                              content={
                                item.validationMessage ??
                                'Possui inconsistência'
                              }
                              position={'left center'}
                              trigger={
                                <span>
                                  <Icon
                                    name="warning sign"
                                    color={
                                      item.validationStatus === 'error'
                                        ? 'red'
                                        : 'yellow'
                                    }
                                    style={{ marginRight: 4 }}
                                  />
                                  {translateStatus(item.status)}
                                </span>
                              }
                            />
                          ) : (
                            translateStatus(item.status)
                          )}
                        </ClickableTableCell>

                        <ClickableTableCell
                          onClick={() => openDetailModal(item)}
                          textAlign={'center'}
                        >
                          {item.value ? `R$ ${item.value}` : '-'}
                          <br />
                          <span style={{ color: '#777' }}>({item.installments}x)</span>
                        </ClickableTableCell>

                        <Table.Cell textAlign={'center'}>
                          <Menu compact>
                            <Menu.Item>
                              <Dropdown
                                trigger={
                                  loadingBudgetPdfId === item.id ? (
                                    <Popup
                                      content={
                                        'Imprimindo orçamento... Por favor, aguarde um momento.'
                                      }
                                      position={'left center'}
                                      trigger={
                                        <Loader active={true} inline size="mini" />
                                      }
                                    />
                                  ) : (
                                    <IconButton name="ellipsis vertical" />
                                  )
                                }
                                icon={null}
                                direction="left"
                              >
                                <Dropdown.Menu style={{ zIndex: 1000 }}>
                                  {!isEstateQuotationUser &&
                                    ableToHire.includes(item.status) && (
                                    <AuthorizationContainer
                                      action={ActionEnum.contract}
                                      feature={FeatureEnum.fireinsurances}
                                    >
                                      <Dropdown.Item
                                        icon="check"
                                        text={'Contratar'}
                                        onClick={() => {
                                          setSendHiring(true);
                                          openDetailModal(item);
                                        }}
                                      />
                                    </AuthorizationContainer>
                                  )}

                                  {ableToEdit.includes(item.status) && (
                                    <Dropdown.Item
                                      icon="pencil alternate"
                                      text={'Editar Orçamento'}
                                      disabled={
                                        item.insurer.code !== InsurersCodeEnum.alfa &&
                                        item.insurer.code !==
                                        InsurersCodeEnum.TokioMarineSegimob &&
                                        item.insurer.code !== InsurersCodeEnum.Yelum
                                      }
                                      onClick={() => {
                                        props.updateCurrentPage({
                                          id: 4,
                                          clickHandler: (e: any, data: any) => { },
                                          name: 'fire-insurance-budget',
                                          route: 'fire-insurance-budget',
                                          text: 'Edição de Cotação',
                                          title: 'Edição de orçamento de seguro incêndio'
                                        });

                                        history.push(
                                          `/admin/fire-insurance-budget/${item?.id}?context=${BudgetContext.editing}&estateId=${item.fireInsurance.estateId}&insurerId=${item?.insurer?.id}`
                                        );
                                      }}
                                    />
                                  )}

                                  <Dropdown.Item
                                    icon="download"
                                    text={'Imprimir Orçamento'}
                                    loading={loadingBudgetPdfId === item.id}
                                    disabled={
                                      (item.insurer.code !== InsurersCodeEnum.alfa &&
                                        item.insurer.code !==
                                        InsurersCodeEnum.TokioMarineSegimob &&
                                        item.insurer.code !== InsurersCodeEnum.Yelum) ||
                                      isCanceled
                                    }
                                    onClick={() => handleFireInsuranceBudgetReport(item)}
                                  />

                                  <Dropdown.Item
                                    icon="trash alternate"
                                    text={'Cancelar Orçamento'}
                                    disabled={isCanceled}
                                    onClick={() => handleOpenCancelModal(item)}
                                  />

                                  {!isEstateQuotationUser && isCanceled && (
                                    <Dropdown.Item
                                      icon="clone alternate"
                                      text={'Clonar Orçamento'}
                                      disabled={
                                        item.insurer.code !== InsurersCodeEnum.alfa
                                      }
                                      onClick={() =>
                                        handleCopyCancelledBudget({
                                          budgetId: item.id,
                                          estateId: item.fireInsurance.estateId,
                                          insurerId: item.insurerId,
                                          context: BudgetContext.copyCanceled
                                        })
                                      }
                                    />
                                  )}

                                  {!isEstateQuotationUser && (
                                    <Dropdown.Item
                                      icon="shield"
                                      text="Cotar Seguro Fiança"
                                      onClick={() => {
                                        redirectToBondInsuranceBudget(
                                          item.fireInsurance.id
                                        );
                                      }}
                                    />
                                  )}
                                  {!isEstateQuotationUser && lenderInsuranceEnabled && (() => {
                                    const tenantDocument = (item.fireInsurance?.tenantDocument || '').replace(/\D/g, '');
                                    const isTenantPf = tenantDocument.length === 11;
                                    if (!isTenantPf) return null;

                                    const tenantBirthDate = item.fireInsurance?.tenantBirthdate || '';
                                    const dateStr = tenantBirthDate instanceof Date
                                      ? tenantBirthDate.toISOString().split('T')[0]
                                      : String(tenantBirthDate);
                                    const tenantAge = dateStr ? calculateAge(dateStr) : 0;
                                    if (tenantAge >= 65) return null;

                                    return (
                                      <Dropdown.Item
                                        icon="handshake"
                                        text={item.fireInsurance.lenderInsuranceId ? "Gerenciar Seguro Prestamista" : "Adicionar Seguro Prestamista"}
                                        onClick={() => handleOpenLenderInsuranceModal(item)}
                                      />
                                    );
                                  })()}
                                </Dropdown.Menu>
                              </Dropdown>
                            </Menu.Item>
                          </Menu>
                        </Table.Cell>
                      </ClickableTableRow>
                    );
                  })}
                </>
              ) : (
                <Table.Row>
                  <Table.Cell textAlign={'center'} colSpan={7}>
                    {getListEmptyMessage(props.user?.role, props.user?.estateUnitId)}
                  </Table.Cell>
                </Table.Row>
              )}
            </>
          )}
        </Table.Body>
        <Table.Footer fullWidth>
          <Table.Row>
            <Table.HeaderCell colSpan={9} textAlign={'right'}>
              {!isLoading && (
                <TablePaginationMenu
                  currentPage={currentPage}
                  dataCount={count}
                  setCurrentPage={setCurrentPage}
                />
              )}
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>

      {openDetail && (
        <DetailModal
          open={openDetail}
          fireInsuranceAnalysis={selectedFireInsurance}
          close={closeDetailModal}
          hire={sendHiring}
        />
      )}

      {openEdit && (
        <EditModal
          open={openEdit}
          fireInsurance={selectedFireInsurance}
          close={closeEditModal}
        />
      )}

      {openCancelModal && (
        <CancelModal
          fireInsurance={selectedFireInsurance}
          open={openCancelModal}
          onClose={closeCancelModal}
        />
      )}

      {openLenderInsuranceModal && selectedFireInsurance && (
        <LenderInsuranceUpdateModal
          open={openLenderInsuranceModal}
          onClose={handleCloseLenderInsuranceModal}
          onSuccess={() => getQuotations()}
          data={selectedFireInsurance}
          rate={lenderInsuranceRate}
          isTenantPF={(selectedFireInsurance?.fireInsurance?.tenantDocument || '').replace(/\D/g, '').length === 11}
        />
      )}

      <PopupModal
        locationActivation={BannerLocationActivationEnum.FIRE_INSURANCE_BUDGET_LIST}
      />
    </>
  );
};

const mapState = (state: iRootState) => ({
  availableBrokersAndEstates: state.availableBrokersAndEstates,
  user: state.user,
  currentPage: state.currentPage
});

const mapDispatch = (dispatch: iRootDispatch) => ({
  updateAvailableBrokersAndEstates: (availableBrokers: AvailableBrokersAndEstatesState) =>
    dispatch.availableBrokersAndEstates.updateAvailableBrokersAndEstates(
      availableBrokers
    ),
  updateCurrentPage: (state: CurrentPageState) =>
    dispatch.currentPage.updateCurrentPage(state)
});

export default connect(mapState, mapDispatch)(BudgetList);
